import * as React from 'react';
import {
  Typography,
  Link,
  TableRow,
  TableCell,
  useMediaQuery,
  Table,
  IconButton,
  TableBody,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import _ from 'lodash';
import axios from 'axios';
import VisibilityIcon from '@mui/icons-material/Visibility';

import GenericSpinner from '../../GenericSpinner/GenericSpinner';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
import { deserializeQueryParamsIntoStr } from '../../../commonFunctions';
import MUIDataTableWrapper from 'components/MUIDataTableWrapper/MUIDataTableWrapper';

// Import Zustand store
import { useAppStore } from '../../../stores';

interface SearchResultItem {
  affiliateGuid: string;
  affiliateName: string;
  locationStateAbr: string;
  affiliateNumber: string;
  affiliateType?: {
    affiliateTypeName: string;
  };
  delegateCount?: number;
  registeredIndividuals?: number;
}

interface IAffiliateSearchResultsProps {
  onRowClick?: (data: unknown) => void;
}

// cancel request if need be
const CancelToken = axios.CancelToken;
let cancel: ((message?: string) => void) | undefined;

const AffiliateSearchResults: React.FC<IAffiliateSearchResultsProps> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const displayMobile = useMediaQuery('(max-width:959px)');

  // Zustand store
  const searchResult = useAppStore(
    (state) => state.search.affiliateSearchResult
  ) as SearchResultItem[];
  const affiliateSearchPending = useAppStore(
    (state) => state.search.affiliateSearchPending
  ) as boolean;
  const setAffiliateSearchPending = useAppStore(
    (state) => state.setAffiliateSearchPending
  );
  const setAffiliateSearchSuccess = useAppStore(
    (state) => state.setAffiliateSearchSuccess
  );
  const setAffiliateSearchError = useAppStore(
    (state) => state.setAffiliateSearchError
  );

  const columns = [
    {
      name: 'affiliateGUID',
      label: 'Affiliate Guid',
      options: {
        display: 'excluded' as const,
      },
    },
    {
      name: 'affiliateName',
      label: 'Affiliate Name',
      options: {
        setCellProps: () => {
          return {
            className: 'onclick-first-cell',
          };
        },
      },
    },
    {
      label: 'Affiliate State',
      name: 'locationStateAbr',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
    {
      label: 'Affiliate Number',
      name: 'affiliateNbr',
    },
    {
      label: 'Affiliate Type',
      name: 'affiliateTypeName',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
    {
      label: 'Delegates',
      name: 'delegates',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
    {
      label: 'Registered Individuals',
      name: 'registeredIndividuals',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
  ];

  const location = useLocation();

  React.useEffect(() => {
    // parse query params into search payload
    const searchPayload = deserializeQueryParamsIntoStr(location.search);

    // make request for the affiliate information
    setAffiliateSearchPending();
    axios
      .post('/api/Affiliate/search', searchPayload, {
        cancelToken: new CancelToken(function executor(c) {
          cancel = c;
        }),
      })
      .then((response) => {
        if (response && response.data) {
          const { data } = response;
          setAffiliateSearchSuccess(data);
        }
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          console.warn('Error: ' + error.message);
        } else {
          setAffiliateSearchError(error);
          enqueueSnackbar(`${error}. Please try again or contact support!`, {
            variant: 'error',
          });
        }
      });
    return () => {
      cancel?.('The compliance search has been cancelled');
    };
  }, []);

  const editButtonOnClick = (e: React.MouseEvent, rowData: unknown) => {
    if (props.onRowClick) {
      props.onRowClick(rowData);
    }
  };

  const resultsDiv = () => {
    if (affiliateSearchPending) {
      return (
        <Grid container direction="row" justifyContent="center">
          <div style={{ padding: '50px' }}>
            <GenericSpinner includeOverlay={false} padding={0} />
          </div>
        </Grid>
      );
    }
    if (searchResult.length == 0) {
      return (
        <Grid container direction="row" justifyContent="center">
          <div style={{ padding: '50px' }}>
            <Typography variant="h5" color="textSecondary" component="h5">
              No results found matching your criteria.
            </Typography>
          </div>
        </Grid>
      );
    }
    return (
      <Grid container direction="row" justifyContent="center" className="w-100 pt-4">
        <MUIDataTableWrapper
          title={'Compliance Search Results'}
          setExpandableRowContent={(rowData: unknown[] | unknown) => {
            return (
              <TableRow>
                <TableCell />
                <TableCell colSpan={3}>
                  <Table>
                    <TableBody className="inner-tbody-expandable-row">
                      <TableRow>
                        <TableCell className="tbl-col-container">
                          <div className="tbl-col-title">View</div>
                          <div>
                            <IconButton
                              aria-label="edit-user"
                              size="small"
                              onClick={(e) => editButtonOnClick(e, rowData)}
                              className="edit-button-expandable-rows"
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="left" className="tbl-col-container">
                          <div className="tbl-col-title"> Affiliate State</div>
                          <div>
                            {(rowData as unknown[])[2] ? String((rowData as unknown[])[2]) : 'N/A'}
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="left" className="tbl-col-container">
                          <div className="tbl-col-title">Affiliate Type</div>
                          <div>
                            {(rowData as unknown[])[4] ? String((rowData as unknown[])[4]) : 'N/A'}
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="left" className="tbl-col-container">
                          <div className="tbl-col-title">Delegates</div>
                          <div>
                            {(rowData as unknown[])[5] ? String((rowData as unknown[])[5]) : 'N/A'}
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="left" className="tbl-col-container">
                          <div className="tbl-col-title">
                            Registered Individuals
                          </div>
                          <div>
                            {(rowData as unknown[])[6] ? String((rowData as unknown[])[6]) : 'N/A'}
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableCell>
              </TableRow>
            );
          }}
          onRowClick={props.onRowClick}
          options={{
            print: false,
            download: false,
            viewColumns: false,
            filter: false,
            rowsPerPageOptions: [5, 10, 15, 25],
            selectableRows: 'none',
          }}
          data={searchResult.map((singleSearchResult: SearchResultItem) => {
            return {
              affiliateGUID: singleSearchResult.affiliateGuid,
              affiliateName: singleSearchResult.affiliateName,
              locationStateAbr: singleSearchResult.locationStateAbr,
              affiliateNbr: singleSearchResult.affiliateNumber,
              affiliateTypeName: singleSearchResult.affiliateType?.affiliateTypeName,
              delegates: singleSearchResult.delegateCount
                ? singleSearchResult.delegateCount
                : 'None',
              registeredIndividuals: singleSearchResult.registeredIndividuals
                ? singleSearchResult.registeredIndividuals
                : 'None',
            };
          })}
          columns={columns}
        />
      </Grid>
    );
  };
  return (
    <Grid container>
      <Grid size={{ xs: 12, md: 12 }}>
        <h2>Compliance Search</h2>
      </Grid>
      <Grid size={{ xs: 12 }} className="mt-4">
        <Link
          href="#"
          style={{ textDecoration: 'underline' }}
          className="back-btn"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            navigate(-1);
          }}
        >
          {'< Back to Search'}
        </Link>
      </Grid>
      <Grid size={{ xs: 12, md: 12 }}>{resultsDiv()}</Grid>
    </Grid>
  );
};

export default AffiliateSearchResults;
